import { roleAccent } from "./organigramaColors";

export const miguelRole = {
  id: "miguel",
  section: "1.2",
  name: "Miguel",
  subtitle: "EXECUTIVE ROLE CHARTER",
  role: "Executive Chair (EC)",
  reportaALabel: "Reports to",
  reportaA: "Shareholders",
  accent: roleAccent("miguel"),
  foco: [],
  noFoco: [],
  propositoLabel: "Purpose of the Role",
  proposito:
    "The Executive Chair is responsible for defining the long-term strategic direction of the Group and ensuring that the organisation has the governance, leadership, financial discipline and organisational capability required to achieve its objectives.\n\nThe role exists to build a scalable, profitable and increasingly autonomous organisation by aligning strategy, structure, governance, people, technology and capital allocation.",
  misionLabel: "Mission",
  mision:
    "Build an organisation capable of sustainable long-term growth through strong governance, organisational excellence and strategic leadership.\n\nThe Executive Chair creates the environment in which the Founder and the COFO can successfully execute their respective responsibilities.",
  scopeLabel: "Scope of Responsibility",
  scopeIntro: "The Executive Chair has executive responsibility for:",
  scopeItems: [
    "Corporate Strategy",
    "Corporate Governance",
    "Organisational Design",
    "Executive Leadership",
    "Capital Allocation",
    "Financial Oversight",
    "Technology Strategy",
    "AI & Digital Transformation",
    "Executive Talent",
    "Succession Planning",
    "Long-term Value Creation",
  ],
  scopeNote:
    "The Executive Chair collaborates closely with the Founder and the COFO to ensure that the organisation remains aligned with the strategic priorities of the Group.",
  responsabilidadesLabel: "Key Responsibilities",
  responsabilidades: [
    {
      title: "1. Executive Leadership",
      intro:
        "Provide strategic leadership across the Group and ensure organisational alignment.",
      items: [
        "Align the Executive Team around the Group's strategic priorities.",
        "Translate the long-term vision into measurable strategic objectives.",
        "Ensure that the Executive Team remains aligned with the strategic priorities of the Group.",
        "Promote collaboration across executive functions.",
        "Resolve strategic conflicts affecting the Group.",
        "Foster a culture of accountability, alignment and continuous improvement.",
      ],
    },
    {
      title: "2. Organisational Strategy",
      intro: "Build an organisation capable of sustainable growth.",
      items: [
        "Design the organisational structure of the Group.",
        "Define governance principles, executive responsibilities and decision-making frameworks.",
        "Clarify ownership and accountability across the organisation.",
        "Reduce organisational bottlenecks.",
        "Improve scalability.",
        "Continuously adapt the organisation to future business needs.",
      ],
    },
    {
      title: "3. Financial Oversight",
      intro: "Ensure the long-term financial strength of the Group.",
      items: [
        "Define the Group's financial strategy together with the COFO.",
        "Approve budgets.",
        "Approve capital allocation and major investments.",
        "Define long-term profitability objectives.",
        "Monitor financial performance.",
        "Ensure that financial discipline supports sustainable growth.",
      ],
    },
    {
      title: "4. Technology & Innovation",
      intro: "Lead the technological transformation of the Group.",
      items: [
        "Lead TON development.",
        "Lead AI strategy and implementation.",
        "Promote process automation.",
        "Drive digital transformation.",
        "Improve reporting capabilities.",
        "Ensure technology becomes a competitive advantage.",
      ],
    },
    {
      title: "5. People & Organisation",
      intro: "Build the leadership organisation required for future growth.",
      items: [
        "Design the future executive organisation.",
        "Approve executive appointments.",
        "Lead succession planning.",
        "Define executive compensation principles together with the COFO.",
        "Develop executive leadership capability.",
        "Reduce dependency on key individuals.",
      ],
    },
    {
      title: "6. Governance",
      intro:
        "Ensure that the Executive Team operates with clear accountability, aligned priorities and effective decision-making.",
      items: [
        "Chair the Executive Management Team.",
        "Approve strategic priorities.",
        "Monitor execution through the Founder and the COFO.",
        "Support strategic decision-making.",
        "Ensure the organisation remains prepared for future growth.",
        "Act as final arbiter whenever strategic alignment or governance requires executive intervention.",
      ],
    },
  ],
  governanceBoundariesLabel: "Governance Boundaries",
  governanceBoundariesIntro:
    "To ensure clear accountability across the Executive Team:",
  governanceBoundaries: [
    "Corporate strategy, governance and organisational design remain the responsibility of the Executive Chair.",
    "Commercial strategy, customer development and product direction remain the responsibility of the Founder & Chief Product, Commercial & Partnerships Officer.",
    "Operational execution, finance and organisational performance remain the responsibility of the COFO.",
    "The Executive Chair provides strategic direction and executive oversight but does not manage the day-to-day commercial or operational activities of the business.",
    "The Executive Chair intervenes directly only where strategic alignment, governance or long-term value creation require executive leadership.",
  ],
  kpiGroups: [
    {
      title: "Financial",
      items: [
        "EBITDA",
        "SDS Profitability",
        "NML Profitability",
        "Cash Flow",
        "Return on Strategic Investments",
      ],
    },
    {
      title: "Organisation",
      items: [
        "Organisational Scalability",
        "Reduction of Structural Costs",
        "Employee Productivity",
        "Reduction of Dependency on Key Individuals",
        "Executive Succession Readiness",
      ],
    },
    {
      title: "Technology",
      items: [
        "TON Implementation",
        "AI Deployment",
        "Process Automation",
        "Digitalisation Progress",
        "Reporting Quality",
      ],
    },
    {
      title: "Governance",
      items: [
        "Executive Team Alignment",
        "Strategic Objectives Achieved",
        "Cross-functional Collaboration",
        "Decision-making Effectiveness",
      ],
    },
  ],
  kpis: [],
  kpisLabel: "Key Performance Indicators",
  leadershipExpectationsLabel: "Leadership Expectations",
  leadershipExpectations:
    "The Executive Chair is expected to build an organisation capable of creating long-term value through governance, strategic clarity and organisational excellence.\n\nLeadership is measured by the ability to align people, strategy and resources while enabling the Founder and the COFO to operate effectively within clearly defined responsibilities.\n\nThe Executive Chair succeeds when the organisation becomes increasingly scalable, disciplined and less dependent on any individual while preserving the entrepreneurial culture that drives innovation and growth.\n\nThe Executive Chair is the custodian of the Group's governance model, ensuring that responsibilities remain clear, decisions are taken at the appropriate level and the organisation evolves without losing alignment, discipline or entrepreneurial spirit.\n\nThe Executive Chair's primary responsibility is not to run the business, but to ensure that the business can be successfully run by the right people, within the right structure and with the right governance.",
  decisionMatrixLabel: "Executive Decision Matrix",
  decisionMatrix: {
    headers: ["Area", "Final Decision", "EC"],
    rows: [
      ["Corporate Strategy", "Executive Chair", "Final Owner"],
      ["Governance", "Executive Chair", "Final Owner"],
      ["Organisational Design", "Executive Chair", "Final Owner"],
      ["Executive Structure", "Executive Chair", "Final Owner"],
      ["Executive Hiring", "Founder & EC", "Final Owner"],
      ["Executive Compensation", "Founder & EC", "Final Owner"],
      ["Capital Allocation", "Executive Chair", "Final Owner"],
      ["Major Investments", "Executive Chair", "Final Owner"],
      ["Financial Strategy", "Executive Chair + COFO", "Final Approval"],
      ["Budget Approval", "Founder & EC", "Final Approval"],
      ["Commercial Strategy", "Founder", "Strategic Oversight"],
      ["Product Strategy", "Founder", "Strategic Oversight"],
      ["Operational Planning", "COFO", "Strategic Oversight"],
      ["Technology Strategy", "Executive Chair", "Final Owner"],
      ["AI Strategy", "Executive Chair", "Final Owner"],
      ["TON", "Executive Chair", "Final Owner"],
    ],
  },
};
